import { $t } from '@/i18n';
/**
 * 侧边栏组件
 *
 * 显示所有合并项的分类列表：
 * - 冲突未解决（红色）
 * - 冲突已解决（橙色）
 * - 代码已合并（绿色）
 */
import { defineComponent, ref, computed, onMounted } from '@vue/composition-api';
import styles from './styles/merge-modal.module.scss';
import { startMouseMove } from '@tencent/ui-core/lib/utils/browser';
import { vueLinkLocalStorage } from '@tencent/ui-core/lib/utils/vue';
import { SIDEBAR_DEFAULT_WIDTH, SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH, SIDEBAR_STORAGE_KEY, } from './constants';
const MergeSidebar = defineComponent({
    name: 'MergeSidebar',
    props: {
        conflicts: {
            type: Array,
            default: () => [],
        },
        mergedPatches: {
            type: Array,
            default: () => [],
        },
        selectDetailChange: {
            type: Object,
            default: null,
        },
    },
    emits: ['change'],
    setup(props, { emit }) {
        const sidebarRef = ref(null);
        const width = ref(SIDEBAR_DEFAULT_WIDTH);
        /** 各区域的折叠状态 */
        const isCollapsed = ref({
            unresolved: false,
            resolved: false,
            merged: false,
        });
        // 将冲突项和已合并项按 sectionType 分类
        const allItems = computed(() => [...props.conflicts, ...props.mergedPatches]);
        const unresolvedConflicts = computed(() => allItems.value.filter(item => item.sectionType === 'unresolved'));
        const resolvedConflicts = computed(() => allItems.value.filter(item => item.sectionType === 'resolved'));
        const mergedPatches = computed(() => allItems.value.filter(item => item.sectionType === 'merged'));
        // 配置三个区域
        const sections = computed(() => [
            {
                type: 'unresolved',
                title: $t('k_c3a416ea'),
                color: 'red',
                items: unresolvedConflicts.value,
            },
            {
                type: 'resolved',
                title: $t('k_d84bdaaa'),
                color: 'orange',
                items: resolvedConflicts.value,
            },
            {
                type: 'merged',
                title: $t('k_ef55ab6f'),
                color: 'green',
                items: mergedPatches.value,
            },
        ].filter(section => section.items.length > 0));
        // 处理项目点击
        const handleItemClick = (item) => {
            emit('change', item);
        };
        // 处理折叠/展开
        const toggleCollapse = (type) => {
            isCollapsed.value[type] = !isCollapsed.value[type];
        };
        /**
         * 处理拖拽调整宽度
         * 使用常量定义的最小和最大宽度
         */
        const handleResizerPointerdown = (ev) => {
            const originalWidth = sidebarRef.value?.offsetWidth;
            if (!originalWidth)
                return;
            ev.preventDefault();
            startMouseMove({
                initialEvent: ev,
                onMove: ({ deltaX }) => {
                    width.value = Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, originalWidth + deltaX));
                },
            });
        };
        // 检查项目是否被选中
        const isItemSelected = (item) => props.selectDetailChange?.key === item.key;
        // 渲染单个项目
        const renderItem = (item, section) => (<div onClick={() => handleItemClick(item)} class={[
                styles.conflict_item,
                isItemSelected(item) ? styles.selected : null,
            ]} key={item.key}>
        <a-tag class="m-0" color={section.color}>
          {item.schemaTitle}
        </a-tag>
        <span class={styles.item_title}>{item.title}</span>
      </div>);
        // 渲染区域
        const renderSection = (section) => (<div class="mb-3" key={section.type}>
        <div class={styles.conflict_title}>
          <a-icon onClick={() => toggleCollapse(section.type)} type="caret-down" class={[
                styles.conflict_title_icon,
                isCollapsed.value[section.type] ? styles.collapse : null,
            ]}/>
          <span class={styles.conflict_title_text}>
            {section.title}
            <span class={styles.conflict_count}>({section.items.length})</span>
          </span>
        </div>
        {!isCollapsed.value[section.type] && (<div>
            {section.items.map(item => renderItem(item, section))}
          </div>)}
      </div>);
        onMounted(() => {
            // 使用 vueLinkLocalStorage 同步宽度到 localStorage
            vueLinkLocalStorage({ width }, SIDEBAR_STORAGE_KEY, 'width');
        });
        return {
            sidebarRef,
            width,
            sections,
            handleResizerPointerdown,
            renderSection,
        };
    },
    render() {
        return (<div class={styles.editor_wrap_left} ref="sidebarRef" style={{ flexBasis: `${this.width}px` }}>
        {this.sections.map(section => this.renderSection(section))}
        <div class={styles.sidebarResizer} onMousedown={this.handleResizerPointerdown}/>
      </div>);
    },
});
export default MergeSidebar;
